import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, combineLatest, distinctUntilChanged, map } from "rxjs";

const STORAGE_KEY = "ga.clientView";

/**
 * Vista de cliente (D-SET32, prototipo v2 del 13/10): un super admin mira la plataforma tal como la ve el
 * cliente, sin cifras internas, intervalos ni metodología. Sirve para enseñar una pantalla delante del
 * cliente, o para comprobar qué le llega, sin pedir a nadie que entre con su usuario.
 *
 * Un solo mecanismo para toda la app:
 *   - el menú de usuario ofrece "Internal / Client" (solo a super admin);
 *   - el marco pone una banda mientras está activa, para que nadie olvide que la tiene puesta;
 *   - cada pantalla que sabe pintarse como la ve el cliente lo declara (`declareSupport`) y pinta con el
 *     rol de cliente mientras está activa.
 *
 * Donde ninguna pantalla montada la soporta, el conmutador no se ofrece y la vista no se aplica: así nunca
 * se promete una vista de cliente que la pantalla de delante no sabe dar.
 *
 * El estado vive en `sessionStorage`: sobrevive a una recarga en la misma pestaña, no a otra pestaña ni a
 * la siguiente sesión. Es una comodidad de quien mira, no un dato de nadie.
 */
@Injectable({ providedIn: "root" })
export class ClientViewService {
    private readonly requested = new BehaviorSubject<boolean>(readStored());
    private readonly supporters = new BehaviorSubject<number>(0);

    /** Alguna pantalla montada sabe pintarse como la ve el cliente. */
    readonly supported$: Observable<boolean> = this.supporters.pipe(
        map((count) => count > 0),
        distinctUntilChanged()
    );

    /** La vista de cliente, pedida y aplicable: solo cuenta si la pantalla de delante la soporta. */
    readonly active$: Observable<boolean> = combineLatest([this.requested, this.supported$]).pipe(
        map(([requested, supported]) => requested && supported),
        distinctUntilChanged()
    );

    /** Lo que se pidió, aplique o no en la pantalla actual. */
    get isRequested(): boolean {
        return this.requested.value;
    }

    setRequested(on: boolean): void {
        this.requested.next(on);
        writeStored(on);
    }

    /**
     * Una pantalla que soporta la vista de cliente se registra al montarse y llama a lo que devuelve al
     * irse. Es un recuento y no un booleano: al navegar, la pantalla nueva puede montarse antes de que se
     * destruya la anterior. Llamar dos veces a la misma baja no descuenta dos.
     */
    declareSupport(): () => void {
        this.supporters.next(this.supporters.value + 1);
        let released = false;

        return () => {
            if (released) return;
            released = true;
            this.supporters.next(Math.max(0, this.supporters.value - 1));
        };
    }
}

function readStored(): boolean {
    try {
        return sessionStorage.getItem(STORAGE_KEY) === "1";
    } catch {
        return false;
    }
}

function writeStored(on: boolean): void {
    try {
        if (on) sessionStorage.setItem(STORAGE_KEY, "1");
        else sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        // Sin almacenamiento (modo privado, bloqueado): la vista sigue funcionando hasta recargar.
    }
}
